<template>
  <ak-card-header>
    <div class="pull-right">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
    <router-link :to="{ name: 'allAgencies' }">
      <h4 class="color-grey">Last Accessed Accounts</h4>
    </router-link>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y">
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell class="width-40">
          <div
            class="circle circle--large bg-grey-lighter c-skeleton-loading__item"
          ></div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          <div class="c-faux-text width-150 c-skeleton-loading__item"></div>
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-display-table v-model:data="filteredAccounts" hover>
      <ak-display-table-row
        v-for="account in filteredAccounts"
        :key="account._id"
        pointer
        @click="goToAccount(account._id)"
      >
        <ak-display-table-cell class="width-40">
          <div class="p-small">
            <ak-avatar
              :src="
                account.logo ||
                'https://res.cloudinary.com/big-top/image/upload/q_auto,f_auto/v1462528865/system-v2/avatars/default-avatar-account.png'
              "
            />
          </div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          {{ account.name || 'No name given' }}
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
  </ak-card-body>
</template>

<script>
import { ref, computed, onMounted, defineComponent } from 'vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { agencyObj } from '@akerolabs/akero-vue/src/store'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'

export default defineComponent({
  name: 'AkWidgetLastAccessed',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },
    /**
     * @type {Object}
     * @description states history accounts
     */
    accounts: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Function}
     * @description Select new account and go to it's dashboard
     */
    goToDashboard: {
      type: Function,
      default: () => ({}),
    },
  },
  setup(props) {
    let n = 10
    const isLoading = ref(true)
    const allAccounts = ref([])

    /**
     * @description Makes an api request to get the accounts
     */
    const fetchAccounts = async () => {
      let accountsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load accounts`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/accounts', {
          params: {
            agency: agencyObj()._id,
            deleted: JSON.stringify({ $ne: true }),
            per_page: 0,
          },
        })
        accountsData = data.data
      } catch (ex) {
        accountsData = []
        displayError(ex)
      }
      allAccounts.value = accountsData
    }

    /**
     * @description Filters history accounts to current agency
     */
    const filteredAccounts = computed(() => {
      const toFilter = JSON.parse(JSON.stringify(props.accounts))
      toFilter.forEach((account, index, object) => {
        if (account.agency !== agencyObj()._id) {
          object.splice(index, 1)
        }
      })
      return toFilter
    })

    /**
     * @description Go to dashboard of selected account with it's full data
     */
    const goToAccount = (id) => {
      const result = allAccounts.value.find(({ _id }) => _id === id)
      props.goToDashboard(result)
    }

    /**
     * @description On mount fetch accounts and filter history accounts
     */
    onMounted(async () => {
      await fetchAccounts()
      isLoading.value = false
    })

    return {
      isOwnAccount,
      n,
      isLoading,
      filteredAccounts,
      goToAccount,
    }
  },
})
</script>
